import React, { SyntheticEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Dropdown from '../../App/Components/Dropdown/Dropdown'
import Spinner from '../../App/Components/Spinner/Spinner'
import DataStoreDataProvider from '../../App/Components/Table/DataProvider/DataStoreDataProvider'
import { default as TableComponent } from '../../App/Components/Table/Table'
import TableColumn from '../../App/Components/Table/TableColumn'
import { useAppDispatch, useAppSelector } from '../../App/Hooks/reduxHooks'
import ProductDataStore from '../DataStore/ProductDataStore'
import { getProductBrands } from '../Store/Actions'

const Table = () => {
  const [dataProvider, setDataProvider] = useState(null)
  const [brandId, setbrandId] = useState(null)
  const { t } = useTranslation()
  const brands = useAppSelector(state => state.products.brands)

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(getProductBrands())
  }, [])

  if (null === dataProvider) {
    setDataProvider(
      new DataStoreDataProvider(
        ProductDataStore,
        'getProducts',
        'patchProduct',
        false,
      ),
    )

    return <Spinner />
  }

  const columns: any[] = []

  let choices: any = {}
  for (const brand of brands) {
    for (const category of brand.categories) {
      choices[`${brand.name} » ${category.name}`] = category.id
    }
  }

  choices = Object.keys(choices)
    .sort()
    .reduce((obj: any, key) => {
      obj[key] = choices[key]

      return obj
    }, {})

  columns.push(
    <TableColumn
      dataKey={'categoryId'}
      editable
      choices={choices}
      collapsable={false}
      key={'columnCategory'}
      sortable>
      {t('Category')}
    </TableColumn>,
  )

  columns.push(
    <TableColumn
      dataKey={'name'}
      editable
      validate={(value: string) =>
        typeof value === 'string' && value.length > 0
      }
      collapsable={false}
      key={'columnName'}
      sortable>
      {t('Name')}
    </TableColumn>,
  )

  columns.push(
    <TableColumn
      dataKey={'purchasePrice'}
      editable
      validate={(value: any) => {
        if (typeof value === 'string')
          value = parseFloat(value.replace(',', '.'))
        return !isNaN(value) && value >= 0
      }}
      transform={(value: any): string => {
        if (isNaN(value) || value === null) return value
        return value.toFixed(2).toString().replace('.', ',')
      }}
      reverseTransform={(value: string): number =>
        parseFloat(value.replace(',', '.'))
      }
      cellStyle={{ textAlign: 'right' }}
      key={'columnPurchasePrice'}
      sortable>
      {t('Purchase price')}
    </TableColumn>,
  )

  columns.push(
    <TableColumn
      dataKey={'salePrice'}
      editable
      validate={(value: any) => {
        if (typeof value === 'string') {
          value = parseFloat(value.replace(',', '.'))
        }

        return !isNaN(value) && value >= 0
      }}
      transform={(value: any): string => {
        if (isNaN(value) || value === null) return value
        return value.toFixed(2).toString().replace('.', ',')
      }}
      reverseTransform={(value: string): number =>
        parseFloat(value.replace(',', '.'))
      }
      cellStyle={{ textAlign: 'right' }}
      key={'columnSalePrice'}
      sortable>
      {t('Sale price')}
    </TableColumn>,
  )

  columns.push(
    <TableColumn
      dataKey={'code'}
      editable
      validate={(value: string) => typeof value === 'string' || null === value}
      collapsed
      key={'columnCode'}
      sortable={false}>
      {t('Code')}
    </TableColumn>,
  )

  columns.push(
    <TableColumn
      dataKey={'barcode'}
      editable
      validate={(value: string) => typeof value === 'string' || null === value}
      collapsed
      key={'columnBarcode'}
      sortable={false}>
      {t('Barcode')}
    </TableColumn>,
  )

  columns.push(
    <TableColumn
      dataKey={'previousBarcode'}
      collapsed
      key={'columnPreviousBarcode'}
      sortable={false}>
      {t('Previous barcode')}
    </TableColumn>,
  )

  columns.push(
    <TableColumn
      dataKey={'inStock'}
      editable
      validate={(value: any) => Number.isInteger(Number(value))}
      cellStyle={{ textAlign: 'right' }}
      collapsed
      key={'columnInStock'}
      sortable
      transform={(value: any): string => {
        if (isNaN(value) || value === null) return '0'

        return value.toString()
      }}
      reverseTransform={(value: string): number => parseInt(value, 10)}>
      {t('In stock')}
    </TableColumn>,
  )

  columns.push(
    <TableColumn
      dataKey={'minStock'}
      editable
      validate={(value: any) =>
        Number.isInteger(Number(value)) && Number(value) >= 0
      }
      cellStyle={{ textAlign: 'right' }}
      collapsed
      key={'columnMinStock'}
      sortable
      transform={(value: any): string => {
        if (isNaN(value) || value === null) return '0'

        return value.toString()
      }}
      reverseTransform={(value: string): number => parseInt(value, 10)}>
      {t('Min. stock')}
    </TableColumn>,
  )

  columns.push(
    <TableColumn
      dataKey={'maxStock'}
      editable
      validate={(value: any) =>
        Number.isInteger(Number(value)) && Number(value) >= 0
      }
      cellStyle={{ textAlign: 'right' }}
      collapsed
      key={'columnMaxStock'}
      sortable
      transform={(value: any): string => {
        if (isNaN(value) || value === null) return '0'

        return value.toString()
      }}
      reverseTransform={(value: string): number => parseInt(value, 10)}>
      {t('Max. stock')}
    </TableColumn>,
  )

  const onBrandChange = (event: SyntheticEvent<HTMLSelectElement>) => {
    let newBrandId = parseInt(event.currentTarget.value, 10)
    if (0 === newBrandId) {
      newBrandId = null
    }

    ProductDataStore.setBrandId(newBrandId)
    setbrandId(newBrandId)
  }

  return (
    <div className={'page-layout large'}>
      <h2>{t('Products')}</h2>
      <Dropdown onChange={onBrandChange} value={null === brandId ? 0 : brandId}>
        <option value={0}>{t('All brands')}</option>
        {brands.map(brand => (
          <option value={brand.id}>{`${brand.name}`}</option>
        ))}
      </Dropdown>
      <TableComponent
        dataProvider={dataProvider}
        pageSize={25}
        version={null === brandId ? 0 : brandId}>
        {columns}
      </TableComponent>
    </div>
  )
}

export default Table
